import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Activity, Eye, Bookmark, MousePointerClick, ExternalLink, Users, TrendingUp } from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PageHeader } from '../components/ui/PageHeader';
import { ErrorAlert } from '../components/ui/ErrorAlert';
import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../api/client';
import {
  getOverview,
  getPinterestConnectUrl,
  listAccounts,
  type AnalyticsOverview,
  type ConnectedAccount,
} from '../api/analytics';

const RANGES = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
] as const;

const METRICS = [
  { key: 'impressions', label: 'Impressions', icon: Eye },
  { key: 'saves', label: 'Saves', icon: Bookmark },
  { key: 'pinClicks', label: 'Pin clicks', icon: MousePointerClick },
  { key: 'outboundClicks', label: 'Outbound clicks', icon: ExternalLink },
] as const;

type MetricKey = (typeof METRICS)[number]['key'];

function rangeFor(days: number): { start: string; end: string } {
  const end = new Date();
  end.setUTCDate(end.getUTCDate() - 1);
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - (days - 1));
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { start: fmt(start), end: fmt(end) };
}

function compact(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${Math.round(n / 1000)}K`;
  if (n >= 1_000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
}

export function AnalyticsPage() {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [accounts, setAccounts] = useState<ConnectedAccount[] | null>(null);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [days, setDays] = useState<number>(30);
  const [metric, setMetric] = useState<MetricKey>('impressions');
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const justConnected = searchParams.get('connected') === '1';

  // Load connected accounts once.
  useEffect(() => {
    let alive = true;
    listAccounts(token)
      .then((accts) => {
        if (!alive) return;
        setAccounts(accts);
        if (accts[0]) setAccountId(accts[0].id);
      })
      .catch((e) => alive && setError(e instanceof ApiError ? e.message : 'Failed to load accounts'));
    return () => {
      alive = false;
    };
  }, [token]);

  // Load overview when account or range changes.
  useEffect(() => {
    if (!accountId) return;
    let alive = true;
    setLoading(true);
    setError(null);
    getOverview(accountId, rangeFor(days), token)
      .then((o) => alive && setOverview(o))
      .catch((e) => alive && setError(e instanceof ApiError ? e.message : 'Failed to load analytics'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [accountId, days, token]);

  const onConnect = useCallback(async () => {
    setConnecting(true);
    setError(null);
    try {
      const { url } = await getPinterestConnectUrl(token);
      window.location.href = url;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not start Pinterest connection');
      setConnecting(false);
    }
  }, [token]);

  const activeMetric = useMemo(() => METRICS.find((m) => m.key === metric)!, [metric]);

  return (
    <>
      <PageHeader
        icon={Activity}
        title="Analytics"
        description="Your Pinterest account performance — impressions, saves, and clicks over time."
      />

      {justConnected && (
        <div className="mb-4 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          Pinterest account connected. Your analytics are below.
          <button
            className="ml-2 underline"
            onClick={() => {
              searchParams.delete('connected');
              setSearchParams(searchParams, { replace: true });
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {error && <ErrorAlert message={error} />}

      {/* No connected account → connect CTA */}
      {accounts !== null && accounts.length === 0 ? (
        <div className="rounded-lg border border-dashed border-ink-200 bg-white px-8 py-16 text-center">
          <Activity className="mx-auto h-8 w-8 text-ink-400" />
          <h2 className="mt-3 text-lg font-semibold text-ink-900">Connect your Pinterest account</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-ink-500">
            Analytics are pulled directly from Pinterest for the account you connect — impressions,
            saves, pin clicks, and outbound clicks. This is your own first-party data.
          </p>
          <button
            onClick={onConnect}
            disabled={connecting}
            className="mt-5 inline-flex items-center gap-2 rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60"
          >
            {connecting ? 'Redirecting…' : 'Connect Pinterest'}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Controls */}
          <div className="flex flex-wrap items-center gap-3">
            {accounts && accounts.length > 1 && (
              <select
                value={accountId ?? ''}
                onChange={(e) => setAccountId(e.target.value)}
                className="rounded-md border border-ink-200 bg-white px-3 py-2 text-sm"
              >
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    @{a.username}
                  </option>
                ))}
              </select>
            )}
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="rounded-md border border-ink-200 bg-white px-3 py-2 text-sm"
            >
              {RANGES.map((r) => (
                <option key={r.days} value={r.days}>
                  {r.label}
                </option>
              ))}
            </select>
            <button
              onClick={onConnect}
              disabled={connecting}
              className="ml-auto rounded-md border border-ink-200 px-3 py-2 text-sm text-ink-700 hover:bg-ink-100 disabled:opacity-60"
            >
              Connect another
            </button>
          </div>

          {/* Profile stats */}
          {overview && (
            <div className="flex flex-wrap gap-6 rounded-lg border border-ink-200 bg-white px-5 py-4">
              <ProfileStat icon={Users} label="Followers" value={compact(overview.profile.followerCount)} />
              <ProfileStat icon={TrendingUp} label="Monthly views" value={compact(overview.profile.monthlyViews)} />
              <ProfileStat icon={Eye} label="Pins" value={compact(overview.profile.pinCount)} />
              <ProfileStat icon={Bookmark} label="Boards" value={compact(overview.profile.boardCount)} />
              <div className="ml-auto self-center text-xs text-ink-400">
                @{overview.profile.username} · {overview.range.start} → {overview.range.end}
              </div>
            </div>
          )}

          {/* Metric cards (click to chart) */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {METRICS.map((m) => {
              const Icon = m.icon;
              const val = overview?.summary[m.key];
              const active = metric === m.key;
              return (
                <button
                  key={m.key}
                  onClick={() => setMetric(m.key)}
                  className={`rounded-lg border px-4 py-3 text-left transition ${
                    active ? 'border-brand-500 bg-brand-50' : 'border-ink-200 bg-white hover:border-ink-300'
                  }`}
                >
                  <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-ink-400">
                    <Icon className="h-4 w-4" /> {m.label}
                  </div>
                  <div className="mt-1 text-2xl font-semibold text-ink-900">
                    {loading && !overview ? '…' : (val ?? 0).toLocaleString()}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Daily chart */}
          <div className="rounded-lg border border-ink-200 bg-white p-4">
            <div className="mb-3 text-sm font-medium text-ink-700">{activeMetric.label} over time</div>
            {overview && overview.daily.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={overview.daily} margin={{ top: 4, right: 8, bottom: 0, left: -12 }}>
                  <defs>
                    <linearGradient id="metricFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6d5efc" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#6d5efc" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: '#94a3b8' }}
                    tickFormatter={(d: string) => d.slice(5)}
                    minTickGap={24}
                  />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} width={44} tickFormatter={compact} />
                  <Tooltip
                    formatter={(v: number) => [v.toLocaleString(), activeMetric.label]}
                    labelStyle={{ color: '#0f172a' }}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                  />
                  <Area
                    type="monotone"
                    dataKey={metric}
                    stroke="#6d5efc"
                    strokeWidth={2}
                    fill="url(#metricFill)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="py-16 text-center text-sm text-ink-400">
                {loading ? 'Loading…' : 'No data for this range yet — publish some pins and check back.'}
              </div>
            )}
          </div>
        </div>
      )}

      {accounts === null && !error && (
        <div className="py-16 text-center text-sm text-ink-400">Loading your accounts…</div>
      )}
    </>
  );
}

function ProfileStat({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Users;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-ink-400" />
      <div>
        <div className="text-xs uppercase tracking-wide text-ink-400">{label}</div>
        <div className="text-lg font-semibold text-ink-900">{value}</div>
      </div>
    </div>
  );
}
